<template>
  <v-row>
    <v-col cols="12">
      <v-tabs class="card-tabs" v-model="tab" touchless>
        <v-tab :href="'#' + TABS.Configuration">
          {{ $t('configuration') }}
          <v-spacer/>
          <div class="ml-2">
            <Icon
              :iconColor="tab === TABS.Configuration ? 'success' : '#aaa'"
              :tooltipColor="tab === TABS.Configuration ? 'success' : '#aaa'"
              icon="mdi-pencil"
              :tooltipText="$t('editConfig')"
              :disabled="readonly"
              @icon-clicked="openCompanyDialog"
            />
          </div>
        </v-tab>
        <v-tab v-if="showSamsara" :href="'#' + TABS.Samsara">
          {{ $t('samsara') }}
          <v-spacer/>
          <div class="ml-2">
            <Icon
              iconColor="success"
              icon="mdi-pencil"
              :tooltipText="$t('editSamsaraConfig')"
              :disabled="readonly || tab !== TABS.Samsara"
              @icon-clicked="openSamsaraDialog"
            />
            <Icon
              :iconColor="samsaraHalted ? 'error' : 'primary'"
              :icon="samsaraHalted ? 'mdi-alert-outline' : 'mdi-history'"
              :small="false"
              :tooltipText="samsaraHalted ? $t('integrationHalted') : $t('integrationRequests')"
              @icon-clicked="openFailuresDialog"
              :disabled="samsaraIconsDisabled"
            />
            <Icon
              :iconColor="tab === TABS.Samsara ? 'black' : '#aaa'"
              icon="mdi-cloud-sync"
              :small="false"
              :tooltipText="$t('syncProductAndAccountTags')"
              :disabled="samsaraIconsDisabled"
              @icon-clicked="trySyncTags"/>
          </div>
        </v-tab>
        <v-tab v-if="showBlobStorage" :href="'#' + TABS.BlobStorage">
          {{ $t('blobStorage') }}
          <v-spacer/>
          <Icon
            v-if="blobStorageHasBadStatus"
            icon="mdi-alert-circle"
            icon-color="error"
            class="ml-1"
            :small="false"
          />
          <div class="ml-2">
            <Icon
              :iconColor="blobLoading ? 'grey' : 'success'"
              :icon="blobLoading ? 'mdi-loading mdi-spin' : 'mdi-pencil'"
              :tooltipText="$t('editBlobStorageConfig')"
              :disabled="readonly || tab !== TABS.BlobStorage || blobLoading"
              @icon-clicked="openBlobStorageDialog"
            />
          </div>
        </v-tab>
        <v-tab v-if="showCustomLayers" :href="'#' + TABS.CustomLayers">
          {{ $t('customLayers') }}
          <v-spacer/>
          <Icon
            v-if="customLayersStatus === ConnectionStringStatus.Error"
            icon="mdi-alert-circle"
            icon-color="error"
            class="ml-1"
            :small="false"
          />
          <div class="ml-2">
            <Icon
              iconColor="success"
              icon="mdi-pencil"
              :tooltipText="$t('editCustomLayersConnection')"
              :disabled="readonly || tab !== TABS.CustomLayers"
              @icon-clicked="openCustomLayersDialog"
            />
          </div>
        </v-tab>
      </v-tabs>
      <v-card style="border-radius: 0px 4px 4px 4px" outlined>
          <v-window v-model="tab">
            <v-window-item :value="TABS.Configuration">
              <LoadableComponent :loading="loading" :height="450">
                <v-col cols="12">
                <v-container>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('companyShortName') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      <span v-if="!config.shortName" @click="openCompanyDialog" class="clickable-text">
                        {{ $t('notSet') }}
                        <Icon
                        icon="mdi-alert-circle-outline"
                        icon-color="error"
                        :tooltipText="$t('clickToSet')"
                        @icon-clicked="openCompanyDialog"/>
                      </span>
                      <span v-else>
                        {{ config.shortName }}
                      </span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('companyTimezone') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      <span v-if="!config.selectedTimeZone" @click="openCompanyDialog" class="clickable-text">
                        {{ $t('notSet') }}
                        <Icon
                        icon="mdi-alert-circle-outline"
                        icon-color="error"
                        :tooltipText="$t('clickToSet')"
                        @icon-clicked="openCompanyDialog"/>
                      </span>
                      <span v-else>
                        {{ config.selectedTimeZone?.name }}
                      </span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('financialIntegration') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      {{ config.financialIntegrationType?.name }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('adjustingEntriesFrequency') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      {{ config.adjustingEntriesFrequency?.name }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('dailyTicketReportTime') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      {{ moment(config.localDailyTicketReportHour, 'H').format('LT') }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('storageReplicationMode') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      {{ config.biReplicationStorageMode?.name ?? $t('notAvailable') }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('endangeredSpeciesMode') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      {{ config.endangeredSpeciesMode?.name ?? $t('notAvailable') }}
                    </v-col>
                  </v-row>
                  <v-row v-if="speciesModeEnabled">
                    <v-col cols="6">
                      {{ $t('dueDiligenceApprovalMode') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      {{ config.dueDiligenceApprovalMode?.name ?? $t('notAvailable') }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('settlementReportMode') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      {{ config.settlementReportMode?.name ?? $t('notAvailable') }}
                    </v-col>
                  </v-row>
                  <v-row v-if="customSettlementMessagesEnabled">
                    <v-col cols="4">
                      {{ $t('defaultSettlementMessage') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="8">
                      <span v-if="!config.defaultSettlementMessage" @click="openCompanyDialog" class="clickable-text">
                        {{ $t('notSet') }}
                        <Icon
                        icon="mdi-alert-circle-outline"
                        icon-color="error"
                        :tooltipText="$t('clickToSet')"
                        @icon-clicked="openCompanyDialog"/>
                      </span>
                      <span v-else class="default-settlement-message">{{ config.defaultSettlementMessage }}</span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="8">
                      {{ $t('requireApprovalForContractModifications') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="4">
                      <Icon
                      :icon="config.requireApprovalForContractModifications ? 'mdi-checkbox-marked-circle' : 'mdi-close'"
                      :iconColor="config.requireApprovalForContractModifications ? 'success' : 'error'"/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('recurringAccountPaymentsCreated') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col cols="6" align="right">
                      {{ recurringAccountPaymentsString }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      {{ $t('integrateWithSamsara') }}:
                    </v-col>
                    <v-spacer/>
                    <v-col align="right" cols="6">
                      <Icon
                      :icon="config.samsaraIntegration ? 'mdi-checkbox-marked-circle' : 'mdi-close'"
                      :iconColor="config.samsaraIntegration ? 'success' : 'error'"/>
                    </v-col>
                  </v-row>
                  <v-row class="mt-2">
                    <v-col cols="12">
                      <h3 class="text-subtitle-1 font-weight-bold">
                        {{ $t('mobileAppSettings') }}
                      </h3>
                    </v-col>
                  </v-row>
                  <v-row class="mt-0 d-flex">
                    <v-col cols="12" md="6">
                      <MobileAppSettingsCard
                        icon="loader"
                        :label="$t('specificAppSettings', { mobileApp: $t('loader') })"
                        :settings="this.loaderSettings"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>
              </LoadableComponent>
            </v-window-item>
            <v-window-item v-if="showSamsara" :value="TABS.Samsara">
              <LoadableComponent :loading="samsaraLoading" :height="200">
                <v-col cols="12">
                  <v-container>
                    <v-row>
                      <v-col cols="2">
                        {{ $t('apiToken') }}:
                      </v-col>
                      <v-spacer/>
                      <v-col cols="10" align="right">
                        <span v-if="missingSamsaraApiKey">
                          <span @click="openSamsaraDialog" class="clickable-text">
                            {{ $t('notSet') }}
                          </span>
                          <Icon
                          icon="mdi-alert-circle-outline"
                          icon-color="error"
                          :tooltipText="$t('clickToSet')"
                          @icon-clicked="openSamsaraDialog"/>
                        </span>
                        <span v-else type="password">
                          {{ apiKeyText }}
                        </span>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="6">
                        {{ $t('trailerIdPrefix1') }}:
                      </v-col>
                      <v-spacer/>
                      <v-col cols="6" align="right">
                        <span v-if="missingSamsaraTrailerIdPrefix1">
                          <span @click="openSamsaraDialog" class="clickable-text">
                            {{ $t('notSet') }}
                          </span>
                          <Icon
                          icon="mdi-alert-circle-outline"
                          icon-color="error"
                          :tooltipText="$t('clickToSet')"
                          @icon-clicked="openSamsaraDialog"/>
                        </span>
                        <span v-else>
                          {{ samsaraConfiguration.samsaraTrailerIdPrefix1 }}
                        </span>
                      </v-col>
                    </v-row>
                    <v-row v-if="samsaraConfiguration.samsaraTrailerIdPrefix2">
                      <v-col cols="6">
                        {{ $t('trailerIdPrefix2') }}:
                      </v-col>
                      <v-spacer/>
                      <v-col cols="6" align="right">
                        {{ samsaraConfiguration.samsaraTrailerIdPrefix2 }}
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        {{ $t('samsaraContact') }}:
                      </v-col>
                      <v-spacer/>
                      <v-col align="right" cols="8">
                        <span v-if="!samsaraConfiguration.samsaraContact?.person?.firstName">
                          <span @click="openSamsaraDialog" class="clickable-text">
                          {{ $t('notSet') }}
                          </span>
                          <span>
                            <Icon
                            icon="mdi-alert-circle-outline"
                            icon-color="error"
                            :tooltipText="$t('clickToSet')"
                            @icon-clicked="openSamsaraDialog"/>
                          </span>
                        </span>
                        <span v-else>
                          {{ samsaraConfiguration.samsaraContact?.person?.firstName }} {{ samsaraConfiguration.samsaraContact?.person?.lastName }}
                          <Icon
                          icon="mdi-phone"
                          iconColor="info"
                          :tooltipText="formattedPhoneNumber"/>
                          <Icon
                          iconColor="info"
                          icon="mdi-email"
                          :tooltipText="samsaraConfiguration.samsaraContact?.person?.email"/>
                        </span>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        {{ $t('status') }}:
                      </v-col>
                      <v-spacer/>
                      <v-col cols="8" align="right">
                        <span>
                          <span :class="samsaraInterrupted ? 'clickable-text' : ''" @click="samsaraInterrupted ? openFailuresDialog() : () => {}">
                          {{ samsaraStatus?.name ?? $t('notAvailable')}}
                          </span>
                          <Icon
                          v-if="samsaraInterrupted"
                          @icon-clicked="openFailuresDialog"
                          icon="mdi-alert"
                          iconColor="error"
                          :tooltipText="$t('integrationHalted')"/>
                        </span>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </LoadableComponent>
            </v-window-item>
            <v-window-item v-if="showBlobStorage" :value="TABS.BlobStorage">
              <v-col cols="12">
                <v-container>
                  <v-row v-for="conn in connectionDisplayItems" :key="conn.name">
                    <v-col cols="6">{{ conn.name }} {{ $t('expires') }}:</v-col>
                    <v-spacer/>
                    <v-col cols="6" align="right">
                      <span v-if="!conn.isSet">
                        <span @click="openBlobStorageDialog" class="clickable-text mr-1">{{ $t('notSet') }}</span>
                        <span><Icon icon="mdi-alert-circle-outline" icon-color="error" :tooltipText="$t('clickToSet')" @icon-clicked="openBlobStorageDialog"/></span>
                      </span>
                      <span v-else class="d-flex align-center justify-end">
                        {{ conn.expiryDisplay || $t('notApplicable') }}
                        <Icon
                          v-if="conn.statusEnum"
                          class="ml-1"
                          :icon="conn.statusEnum === ConnectionStringStatus.Active ? 'mdi-checkbox-marked-circle' : conn.statusEnum === ConnectionStringStatus.Error ? 'mdi-alert-circle' : 'mdi-clock-outline'"
                          :iconColor="conn.statusColor"
                          :tooltipText="conn.statusEnum !== ConnectionStringStatus.Pending && conn.statusReason ? conn.statusReason : conn.statusEnum.name"/>
                      </span>
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-window-item>
            <v-window-item v-if="showCustomLayers" :value="TABS.CustomLayers">
              <v-col cols="12">
                <v-container>
                  <v-row>
                    <v-col cols="6">{{ $t('customLayersConnection') }}:</v-col>
                    <v-spacer/>
                    <v-col cols="6" align="right">
                      <span v-if="!customMapLayersConnection">
                        <span @click="openCustomLayersDialog" class="clickable-text mr-1">{{ $t('notSet') }}</span>
                        <span><Icon icon="mdi-alert-circle-outline" icon-color="error" :tooltipText="$t('clickToSet')" @icon-clicked="openCustomLayersDialog"/></span>
                      </span>
                      <span v-else class="d-flex align-center justify-end">
                        {{ $t('set') }}
                        <Icon
                          class="ml-1"
                          :icon="customLayersStatus === ConnectionStringStatus.Active ? 'mdi-checkbox-marked-circle' : customLayersStatus === ConnectionStringStatus.Error ? 'mdi-alert-circle' : 'mdi-clock-outline'"
                          :iconColor="customLayersStatus === ConnectionStringStatus.Active ? 'success' : customLayersStatus === ConnectionStringStatus.Error ? 'error' : 'warning'"
                          :tooltipText="customMapLayersConnection.statusReason || customLayersStatus.name"/>
                      </span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="text-caption grey--text text--darken-1">{{ $t('customLayersConnectionHint') }}</v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-window-item>
          </v-window>
        </v-card>
    </v-col>
    <Dialog :stateId="integrationsDialogId" @dialog-closing="closeDialog" @close="closeDialog">
      <IntegrationRecords
      v-if="integrationFailureDialog"
      :readonly="readonly"
      :integrationStatus="SamsaraIntegrationStatus.forInt(samsaraConfiguration.integrationStatus)"
      :integrationStatusReason="SamsaraIntegrationStatusReason.forInt(samsaraConfiguration.integrationStatusReason)"
      :unsavedChanges="unsavedChanges"
      @patchIntegration="patchIntegration"/>
      <CompanyConfigForm
      v-if="editingConfig"
      @close="closeDialog"
      :propOptions="config"
      @done-editing="doneEditing"/>
      <SamsaraConfigForm
      :propOptions="samsaraConfiguration"
      v-if="editingSamsara"
      @close="closeDialog"
      @done-editing="doneEditingSamsara"
      :isEditing="samsaraConfigExists"/>
      <BlobStorageConfigForm
      v-if="editingBlobStorage"
      :propConfig="blobStorageConfig"
      @close="closeDialog"
      @done-editing="doneEditingBlobStorage"/>
      <CustomLayersConnectionForm
      v-if="editingCustomLayers"
      @close="closeDialog"
      @done-editing="doneEditingCustomLayers"/>
    </Dialog>
  </v-row>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { SamsaraIntegrationStatus, SamsaraIntegrationStatusReason, StorageReplicationMode, ConnectionStringStatus, SettlementEmailMessageMode, EndangeredSpeciesMode } from '@/utils/Enumerations'

const TABS = Object.freeze({
  Configuration: 'configuration',
  Samsara: 'samsara',
  BlobStorage: 'blobStorage',
  CustomLayers: 'customLayers'
})

export default {
  name: 'CompanyOptions',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    CompanyConfigForm: () => import('@/components/settings-components/company-config/CompanyConfigForm.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    SamsaraConfigForm: () => import('@/components/settings-components/company-config/SamsaraConfigForm.vue'),
    IntegrationRecords: () => import('@/components/settings-components/company-config/IntegrationRecords.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    MobileAppSettingsCard: () => import('@/components/settings-components/company-config/MobileAppSettingsCard.vue'),
    BlobStorageConfigForm: () => import('@/components/settings-components/company-config/BlobStorageConfigForm.vue'),
    CustomLayersConnectionForm: () => import('@/components/settings-components/company-config/CustomLayersConnectionForm.vue')
  },

  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      required: true
    },
    customConnections: {
      type: Array,
      default: () => ([])
    },
    customMapLayersConnection: {
      type: Object,
      default: null
    },
    samsaraHalted: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    unsavedChanges: {
      type: Boolean,
      required: true
    }
  },

  data: () => ({
    SamsaraIntegrationStatus,
    SamsaraIntegrationStatusReason,
    StorageReplicationMode,
    ConnectionStringStatus,
    TABS,
    tab: TABS.Configuration,
    editingConfig: false,
    editingSamsara: false,
    editingBlobStorage: false,
    editingCustomLayers: false,
    hideKey: true,
    integrationFailureDialog: false,
    integrationsDialogId: uniqueDialogId('companyInfoDialog'),
    samsaraConfigExists: false,
    samsaraLoading: false,
    blobLoading: false,
    hasFetchedBlobConfig: false,
    blobStorageConfig: [],
    fetchedBlobConfig: []
  }),

  watch: {
    async tab (tab) {
      const refreshSamsara = (this.samsaraConfigExists && tab === TABS.Samsara)
      if (refreshSamsara) {
        await this.refreshSamsara()
      }
    },

    loading (val, oldVal) {
      if (!oldVal) return
      this.samsaraConfigExists = this.config.samsaraIntegration
      this.blobStorageConfig = []
      this.fetchedBlobConfig = []
      this.hasFetchedBlobConfig = false
    }
  },

  created () {
    this.samsaraConfigExists = this.config.samsaraIntegration
  },

  computed: {
    ...mapGetters('samsara', ['samsaraConfiguration']),
    showSamsara () {
      return this.config.samsaraIntegration && !this.readonly
    },

    showBlobStorage () {
      return this.config.biReplicationStorageMode?.value === StorageReplicationMode.BlobStorage.value && !this.readonly
    },

    customSettlementMessagesEnabled () {
      return this.config.settlementEmailMessageMode?.value === SettlementEmailMessageMode.Default.value
    },

    speciesModeEnabled () {
      return this.config.endangeredSpeciesMode?.value !== EndangeredSpeciesMode.None.value
    },

    defaultConnections () {
      return [
        { name: this.$t('storageConnectionString1'), connectionString: '' },
        { name: this.$t('storageConnectionString2'), connectionString: '' }
      ]
    },

    connectionDisplayItems () {
      const sources = this.customConnections.length > 0
        ? this.customConnections
        : this.blobStorageConfig.length > 0
          ? this.blobStorageConfig.map(b => ({ name: b.name, status: null, statusReason: null, expiresAt: null }))
          : this.defaultConnections.map(b => ({ name: b.name, status: null, statusReason: null, expiresAt: null }))

      return sources.map(cc => {
        const local = this.blobStorageConfig.find(b => b.name === cc.name)
        const fetched = this.fetchedBlobConfig.find(b => b.name === cc.name)
        const localCs = local?.connectionString ?? ''
        const fetchedCs = fetched?.connectionString ?? ''
        const changed = localCs && localCs !== fetchedCs
        const statusEnum = changed ? ConnectionStringStatus.Pending : ConnectionStringStatus.forInt(cc.status)
        const isSet = !!localCs || cc.status != null

        let expiryDisplay = null
        if (localCs) {
          const localExpiry = this.parseSasExpiry(localCs)
          expiryDisplay = localExpiry ? moment(localExpiry).format('LLL') : null
        } else if (cc.expiresAt) {
          expiryDisplay = moment(cc.expiresAt).format('LLL')
        }

        let statusColor = 'grey'
        if (statusEnum === ConnectionStringStatus.Active) statusColor = 'success'
        else if (statusEnum === ConnectionStringStatus.Error) statusColor = 'error'
        else if (statusEnum) statusColor = 'warning'

        return {
          name: cc.name,
          isSet,
          expiryDisplay,
          statusEnum,
          statusReason: cc.statusReason,
          statusColor
        }
      })
    },

    blobStorageHasBadStatus () {
      return this.connectionDisplayItems.some(c => c.statusEnum === ConnectionStringStatus.Error)
    },

    showCustomLayers () {
      return !this.readonly && !!this.config.useCustomMapLayers
    },

    customLayersStatus () {
      return this.customMapLayersConnection ? ConnectionStringStatus.forInt(this.customMapLayersConnection.status) : null
    },

    missingSamsaraApiKey () {
      return !this.samsaraConfiguration.apiTokenSuffix
    },

    missingSamsaraTrailerIdPrefix1 () {
      return !this.samsaraConfiguration.samsaraTrailerIdPrefix1
    },

    apiKeyText () {
      const numBullets = 41
      const prefix = this.samsaraConfiguration.apiTokenSuffix
      return `${'\u2022'.repeat(numBullets)}${prefix}`
    },

    recurringAccountPaymentsString () {
      if (this.config.accountPayableTemplateDayOfWeek == null) return this.$t('notAvailable')
      const time = moment(this.config.accountPayableTemplateLocalHour, 'H').format('LT')
      const dayOfWeek = this.config.accountPayableTemplateDayOfWeek?.name
      return this.$t('pluralAtTime', { object: dayOfWeek, time })
    },

    formattedPhoneNumber () {
      const number = this.samsaraConfiguration.samsaraContact?.person?.phoneNumber
      return /^\d{3}\d{3}\d{4}/.test(number) ? number.replace(/(\d{3})(\d{3})(\d{4})/, '($1)-$2-$3') : number
    },

    samsaraIconColor () {
      if (this.samsaraHalted) return 'error'
      return this.tab === TABS.Samsara ? 'primary' : '#aaa'
    },

    samsaraIconsDisabled () {
      return this.tab !== TABS.Samsara || this.readonly || this.samsaraConfiguration.integrationStatus == null
    },

    samsaraStatus () {
      return SamsaraIntegrationStatus.forInt(this.samsaraConfiguration.integrationStatus)
    },

    samsaraInterrupted () {
      return this.samsaraStatus === SamsaraIntegrationStatus.Interrupted
    },

    loaderSettings () {
      return [
        { label: this.$t('filterContractsToLoaderAccount'), value: this.config.filterContractsToLoaderAccount }
      ]
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('user', ['fetchCustomConnectionStrings']),
    ...mapActions('samsara', ['fetchSamsaraConfiguration',
      'createSamsaraConfiguration',
      'updateSamsaraConfiguration',
      'syncTags',
      'patchSamsaraConfiguration',
      'setSamsaraConfiguration']),
    ...mapMutations('samsara', ['mutateSamsaraConfiguration']),
    moment,
    parseSasExpiry (connectionString) {
      if (!connectionString) return null
      const match = connectionString.match(/se=([^&;]+)/i)
      if (!match) return null
      try {
        const date = new Date(decodeURIComponent(match[1]))
        return isNaN(date.getTime()) ? null : date
      } catch {
        return null
      }
    },
    doneEditing (config) {
      if (config.samsaraIntegration && !this.config.samsaraIntegration) this.tab = TABS.Samsara
      this.closeDialog()
      this.$emit('config-changed', config)
    },
    doneEditingSamsara (samsara) {
      this.mutateSamsaraConfiguration(samsara)
      this.closeDialog()
    },
    doneEditingBlobStorage (blobConfig) {
      this.blobStorageConfig = blobConfig
      this.closeDialog()
      const hasChanges = blobConfig.some(c => {
        const fetched = this.fetchedBlobConfig.find(f => f.name === c.name)
        return c.connectionString !== (fetched?.connectionString ?? '')
      })
      if (hasChanges) {
        this.$emit('blob-config-changed', blobConfig)
      }
    },
    doneEditingCustomLayers () {
      this.closeDialog()
      this.$emit('refresh-companyInfo')
    },
    closeDialog () {
      this.integrationFailureDialog = false
      this.editingConfig = false
      this.editingSamsara = false
      this.editingBlobStorage = false
      this.editingCustomLayers = false
      this.closeDialogsAtOrAbove(this.integrationsDialogId)
    },
    openCustomLayersDialog () {
      this.editingCustomLayers = true
      this.openOrUpdateDialog({ id: this.integrationsDialogId, width: '700' })
    },
    openFailuresDialog () {
      this.integrationFailureDialog = true
      this.openOrUpdateDialog({ id: this.integrationsDialogId, width: '35vw' })
    },
    openSamsaraDialog () {
      this.editingSamsara = true
      this.openOrUpdateDialog({ id: this.integrationsDialogId, width: '500' })
    },
    async openBlobStorageDialog () {
      if (!this.hasFetchedBlobConfig) {
        try {
          await this.loadCustomConnectionStrings()
        } catch {
          // Swallow — dialog opens with empty fields so user can still enter strings
        }
      }
      if (this.blobStorageConfig.length === 0) {
        const source = this.customConnections.length > 0 ? this.customConnections : this.defaultConnections
        this.blobStorageConfig = source.map(cc => ({
          name: cc.name,
          connectionString: '',
          customConnectionId: cc.customConnectionId ?? null
        }))
      }
      this.editingBlobStorage = true
      this.openOrUpdateDialog({ id: this.integrationsDialogId, width: '700' })
    },
    openCompanyDialog () {
      this.editingConfig = true
      this.openOrUpdateDialog({ id: this.integrationsDialogId, width: '800' })
    },
    async loadCustomConnectionStrings () {
      this.blobLoading = true
      try {
        const data = await this.fetchCustomConnectionStrings()
        const items = (Array.isArray(data) ? data : (data?.connections ?? []))
          .filter(c => this.customConnections.some(cc => cc.customConnectionId === c.customConnectionId))
        this.blobStorageConfig = items.map(c => ({
          name: this.customConnections.find(cc => cc.customConnectionId === c.customConnectionId)?.name ?? c.name,
          connectionString: c.connectionString ?? '',
          customConnectionId: c.customConnectionId ?? null
        }))
        this.fetchedBlobConfig = this.blobStorageConfig.map(c => ({ ...c }))
        this.hasFetchedBlobConfig = true
      } finally {
        this.blobLoading = false
      }
    },
    async refreshSamsara () {
      this.samsaraLoading = true
      await this.fetchSamsaraConfiguration()
      this.samsaraLoading = false
    },
    async trySyncTags () {
      if (this.unsavedChanges) {
        this.setSnackError(this.$t('mustSaveChangesBeforeSync'))
        return
      }
      await this.syncTags()
    },
    async patchIntegration () {
      const body = [{
        op: 'add',
        path: '/integrationstatus',
        value: SamsaraIntegrationStatus.Active.value
      }]

      await this.patchSamsaraConfiguration(body)
      await this.refreshSamsara()
      this.$emit('refresh-companyInfo')
    }
  }
}

</script>
